// BCM-898 · Goods Receipt Dashboard — a single batch row + expandable action.
// Eight columns (slide 5): Batch Number (+ pending-action label beneath), Material
// Code/Description, PO Number, CMO, Date of Receipt, Batch Qty, UoM, View Info.
// A chevron column precedes them ONLY when the result holds more than one row.
// Expanding lazily loads A2 detail and mounts the resolved Action section beneath.
import React, { useEffect, useState } from "react";
import {
  TableRow,
  TableCell,
  IconButton,
  Typography,
  Collapse,
  Box,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import ActionSection from "./ActionSection";
import ViewInfoIcons from "./ViewInfoIcons";
import { presentFormatedData, presentDate } from "../../functionalComponents/dataFormatFunctions";
import { trimQty } from "../utils/formatters";
import { labelFor } from "../utils/resolvePendingAction";
import { useGRStyles } from "../goodsReceiptStyles";

const BatchRow = ({
  row,
  showChevron,
  expanded,
  onToggle,
  pendingLabel,
  detail,
  detailLoading,
  detailError,
  featuresAccess,
  qualityApprovalFlowFlag,
  onRefresh,
  rowRef,
  colSpan,
}) => {
  const classes = useGRStyles();

  // resolvePendingAction resolves the row's label synchronously (row + flags
  // only), so a batch whose generation flag is already true reads "IBD
  // generation pending" / "GRN generation pending" even when the Pre-IBD/Pre-GRN
  // checks (re-run async by ActionSection's PA9 gate) turn out to still be
  // failing — in which case the action box below actually renders PA8's failure
  // screen instead. `resolvedPa` is that gate's real answer, reported once it
  // settles; it overrides the label so the two always agree. It only ever
  // reflects THIS row's own latest check, and resets whenever the row collapses
  // so a stale verdict from a previous expand never lingers into the next one.
  const [resolvedPa, setResolvedPa] = useState(null);
  useEffect(() => {
    if (!expanded) setResolvedPa(null);
  }, [expanded]);
  // PA2's label (incl. "Review pending" → "N Low Confidence Fields" / the ASN
  // wording once `detail` loads) is decided by resolvePendingAction, so
  // `pendingLabel` is already final here.
  const displayLabel = resolvedPa ? labelFor(resolvedPa, row, detail) : pendingLabel;

  return (
    <React.Fragment>
      <TableRow ref={rowRef}>
        {showChevron && (
          <TableCell padding="checkbox">
            <IconButton size="small" onClick={onToggle} aria-label="expand batch">
              {expanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            </IconButton>
          </TableCell>
        )}

        <TableCell align="left">
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            {presentFormatedData(row.vendorBatchNumber)}
          </Typography>
          {displayLabel && (
            <Typography variant="caption" style={{ color: "#8A93A1", display: "block" }}>
              {displayLabel}
            </Typography>
          )}
        </TableCell>

        <TableCell align="left">
          {presentFormatedData(row.fgMaterialCode)} /<br />
          {presentFormatedData(row.fgMaterialName)}
        </TableCell>

        <TableCell align="center">
          {presentFormatedData(row.scheduleAgreementNumber)}
        </TableCell>

        <TableCell align="left">{presentFormatedData(row.vendorName)}</TableCell>

        <TableCell align="center">{presentDate(row.dateOfReceipt)}</TableCell>

        <TableCell align="right">{presentFormatedData(trimQty(row.batchQty))}</TableCell>

        <TableCell align="right">{presentFormatedData(row.batchUOM)}</TableCell>

        <TableCell align="center">
          <ViewInfoIcons
            row={row}
            featuresAccess={featuresAccess}
            qualityApprovalFlowFlag={qualityApprovalFlowFlag}
            onRefresh={onRefresh}
          />
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ padding: 0, backgroundColor: "#FAFAFA" }} colSpan={colSpan}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box>
              {detailError && !detailLoading && (
                <div className={classes.errorLine}>
                  <WarningRoundedIcon style={{ fontSize: 18 }} />
                  {detailError}
                </div>
              )}
              <ActionSection
                row={row}
                detail={detail}
                detailLoading={detailLoading}
                featuresAccess={featuresAccess}
                onRefresh={onRefresh}
                onActionResolved={setResolvedPa}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

export default BatchRow;
